import css from "./stepdown.module.scss";
import { Layout } from "../../componentsV1/layout";
import { Pill } from "../../componentsV1/Pill";
import { Card } from "../../componentsV1/Card";
import Logo from "../../assets/v1/logo.png";
import { Antibody, Stack, Graph, Target, Link, CheckCircle } from "../../assets/v1/icons";
import { Widefield, WesternBlot, WidefieldSIM, Airyscan, Confocal, STORM, SIM, SIM2 } from "../../assets/v1/stepdown";
import ImageViewer from "awesome-image-viewer";

export const Stepdown = () => {

  const steps = ["WB", "Widefield", "WF-SIM", "Confocal", "Airyscan", "SIM", "SIM²", "STORM"];
  const stepDown = [
    {
      title: 'Biochemical Validation',
      question: 'Is the antibody biochemically correct before imaging begins?',
      insight: 'Western blot confirms specificity and expected molecular weight before any spatial interpretation is attempted.',
      image: WesternBlot
    },
    {
      title: 'Widefield',
      question: 'Is the signal present and damage-associated?',
      insight: 'Widefield establishes baseline localization and recruitment but provides no structural interpretation.',
      image: Widefield
    },
    {
      title: 'Widefield SIM',
      question: 'Is the spatial relationship truly three-dimensional?',
      insight: 'Optical sectioning and wedge views confirm co-distribution in volume, eliminating projection artifact without claiming nanoscale structure.',
      image: WidefieldSIM
    },
    {
      title: 'Confocal',
      question: 'Where is the signal localized under controlled acquisition?',
      insight: 'Confocal defines reproducible foci and enables quantification, while remaining diffraction-limited.',
      image: Confocal
    },
    {
      title: 'Airyscan',
      question: 'Can the signal be seen more clearly?',
      insight: 'Airyscan improves clarity and morphology, but not true subunit resolution.',
      image: Airyscan
    },
    {
      title: 'SIM',
      question: 'Is there heterogeneity within the assemblies?',
      insight: 'SIM begins to separate puncta into spatially distinct features, suggesting underlying organization.',
      image: SIM
    },
    {
      title: 'SIM²',
      question: 'Are recurring patterns emerging?',
      insight: 'SIM² reveals more refined motifs, but these remain vulnerable to conflation and over-interpretation.',
      image: SIM2
    },
    {
      title: 'STORM',
      question: 'What begins to represent nanoscale truth?',
      insight: 'STORM resolves smaller substructures and reframes lower-resolution patterns as approximations rather than direct structure.',
      image: STORM
    },
  ]

  const applications = [
    {
      subtitle: 'Biochemical Validation',
      title: 'Western Blot',
      description: 'Confirms specificity, expected molecular weight, and biochemical correctness before spatial interpretation begins.',
      answer: 'Validates the target before imaging.'
    },
    {
      subtitle: 'Baseline Localization',
      title: 'Widefield Microscopy',
      description: 'Rapidly establishes signal presence, recruitment, and general localization across the cell population.',
      answer: 'Answers whether signal is present and damage-associated.'
    },
    {
      subtitle: 'Volumetric Context',
      title: 'WF-SIM',
      description: 'Adds optical sectioning and three-dimensional context to distinguish real volume from projection artifact.',
      answer: 'Clarifies whether the spatial relationship is volumetrically real.'
    },
    {
      subtitle: 'Controlled Acquisition',
      title: 'Confocal',
      description: 'Produces reproducible, diffraction-limited localization data suitable for quantification and comparative analysis.',
      answer: 'Defines controlled localization and foci behavior.'
    },
    {
      subtitle: 'Enhanced Clarity',
      title: 'Airyscan',
      description: 'Improves morphology and signal clarity while preserving confidence in acquisition discipline.',
      answer: 'Improves visibility without overstating resolution.'
    },
    {
      subtitle: 'Structural Separation',
      title: 'SIM',
      description: 'Begins resolving heterogeneity within assemblies and separates features that merge at lower resolution.',
      answer: 'Reveals substructure trends and organizational differences.'
    },
    {
      subtitle: 'Refined Patterning',
      title: 'SIM²',
      description: 'Pushes structured illumination further to expose recurring motifs and more refined internal organization.',
      answer: 'Strengthens structural interpretation before nanoscale methods.'
    },
    {
      subtitle: 'Nanoscale Truth',
      title: 'STORM',
      description: 'Resolves substructures at the nanoscale and reframes lower-resolution patterns as approximations rather than direct structure.',
      answer: 'Defines what begins to represent nanoscale biological truth.'
    },
  ]

  const logos = [
    {
      icon: Stack,
      text: "Cross-Scale Integration"
    },
    {
      icon: Graph,
      text: "Resolution Transition Control"
    },
    {
      icon: CheckCircle,
      text: "Antibody Performance Validation"
    },
    {
      icon: Link,
      text: "Deterministic Signal Output"
    },
  ]

  return (
    <Layout>
      <div className={css.heroContainer}>
        <div className={css.heroContent}>
          <img src={Logo} alt="logo" className={css.heroLogo} />
          <div className={css.heroText}>
            <h1>STEPDOWN™</h1>
            <div className={css.line} />
            <p>Stepdown™ establishes a patented framework for cross-scale imaging and signal validation.</p>
            <p>It defines how data is acquired, registered, and interpreted across modalities—preserving signal integrity through resolution transitions while validating antibody specificity, localization, and performance within intact cellular systems. Cross-Scale Integration Resolution Transition Control Antibody Performance Validation Deterministic Signal Output</p>
          </div>
          <div className={css.logoContainer}>
            {logos.map((item) => {
              return (
                <div className={css.logo}>
                  <img src={item.icon} alt={item.text} />
                  <p>{item.text}</p>
                </div>
              )
            })
            }
          </div>
        </div>
      </div>
      <div className={css.container}>
        <div className={css.section}>
          <section className={css.sectionBg}>
            <div className={css.subHeading}>TECHNOLOGY PHILOSPHY</div>
            <h2 className={css.heading}>We use resolution as a decision framework, not a marketing term.</h2>
            <p>A sharper image alone does not guarantee a better answer. Identifyn sequences modalities so biochemical correctness, localization, volumetric confidence, structural separation, and nanoscale interpretation build on one another in the correct order.</p>
          </section>
        </div>
        <div className={css.section}>
          <section className={css.sectionBg}>
            <div className={css.subHeading}>CORE PRINCIPLE</div>
            <h2 className={css.heading}>If you cannot resolve it, you cannot define it—only approximate it.</h2>
          </section>
        </div>
        <div className={css.applicationContainer}>
          {applications.map((app, index) => (
            <ApplicationCard key={index} number={index + 1} title={app.title} subtitle={app.subtitle} answer={app.answer} description={app.description} />
          ))}
        </div>
        <div className={css.stepContainer}>
          {stepDown.map((step, index) => {
            const sortedImages = [
              { mainUrl: step.image },
              ...stepDown
                .filter((_, i) => i !== index)
                .map(item => ({
                  mainUrl: item.image,
                })),
            ];

            return (
              <StepCard
                key={index}
                number={index + 1}
                title={step.title}
                question={step.question}
                insight={step.insight}
                image={step.image}
                images={sortedImages}
              />
            );
          })}
        </div>
      </div>
    </Layout>
  );
}


const StepCard = ({ number, title, question, insight, image, images }) => (
  <div className={`${css.sectionBg} ${css.stepCard}`}>
    <div className={css.infoContainer}>
      <div className={css.infoIcon}>{number}</div>
      <div className={css.infoHeading}>{title}</div>
      <div className={css.infoDescription}>{question}</div>
    </div>

    <div className={css.imagePanel} onClick={() => {
      new ImageViewer({
        images,
        closeOnEsc: true,
        isZoomable: true,
        closeOnOutsideClick: true
      })
    }}>
      <i className="fal fa-expand-arrows-alt"></i>
      {image ? (
        <img src={image} alt={title} className={css.image} />
      ) : (
        <div className={css.text}>Add image</div>
      )}
    </div>
    <Card heading={'Key insigt'} description={insight} className={css.insightCard} />
  </div>
);

const ApplicationCard = ({ title, subtitle, description, answer, number }) => (
  <div className={`${css.sectionBg} ${css.applicationCard}`}>
    <div className={css.header}>
      <div className={css.leftCol}>
        <div className={css.appSubHeading}>{subtitle}</div>
        <div className={css.appHeading}>{title}</div>
      </div>
      <div className={css.number}>{number}</div>
    </div>
    <div className={css.appDescription}>{description}</div>
    <Card heading={'What it answers'} description={answer} className={css.answerCard} />
  </div>
);